import {
  type CreateLockerversePublicEventsOptions,
  createLockerversePublicEventsClient,
  type LockerversePublicEvent,
  type LockerversePublicEventsClient,
  type LockerversePublicEventsPage,
  type LockerversePublicEventsSort,
  type LockerverseSdkError,
} from "@lockerverse/sdk/public-events";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImmutableResourceIdentity } from "./immutable-resource-identity.ts";

export type LockerversePublicEventsConfig = {
  /** Show separate current and past views. Defaults to true. */
  showTabs?: boolean;
  /** Make past Events available. Defaults to true. */
  includePast?: boolean;
  /** Inclusive lower bound of the event overlap window, as an ISO instant. */
  from?: string | undefined;
  /** Exclusive upper bound of the event overlap window, as an ISO instant. */
  to?: string | undefined;
  sort?: LockerversePublicEventsSort | undefined;
};

export type UseLockerversePublicEventsOptions = {
  /** Initial legacy public SDK snapshot. The hook still refreshes on mount. */
  initialEvents?: LockerversePublicEvent[];
  /** Initial search page for a configured list. The hook still refreshes on mount. */
  initialPage?: LockerversePublicEventsPage;
  config?: LockerversePublicEventsConfig;
};

export type UseLockerversePublicEventsResult = {
  client: LockerversePublicEventsClient;
  config: Required<
    Pick<LockerversePublicEventsConfig, "showTabs" | "includePast">
  > &
    Pick<LockerversePublicEventsConfig, "from" | "to" | "sort">;
  events: LockerversePublicEvent[] | null;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: LockerverseSdkError | null;
  refresh: () => void;
  loadMore: () => void;
};

type ListingState = Pick<
  UseLockerversePublicEventsResult,
  "events" | "hasMore" | "loading" | "loadingMore" | "error"
>;

/** Load the public events list for one community. */
export function useLockerversePublicEvents(
  options: CreateLockerversePublicEventsOptions,
  { initialEvents, initialPage, config }: UseLockerversePublicEventsOptions = {}
): UseLockerversePublicEventsResult {
  useImmutableResourceIdentity("public events", {
    apiBaseUrl: options.apiBaseUrl,
    communitySlug: options.communitySlug,
    environment: options.environment,
    slug: "",
  });
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const [client] = useState(() =>
    createLockerversePublicEventsClient({
      ...options,
      onError: (error) => onErrorRef.current?.(error),
    })
  );
  const showTabs = config?.showTabs ?? true;
  const includePast = config?.includePast ?? true;
  const from = config?.from;
  const to = config?.to;
  const sort = config?.sort;
  const searchSort =
    sort ?? (!showTabs && includePast ? "startAtDesc" : "startAtAsc");
  const usesSearch = !showTabs || Boolean(from || to || sort);
  const timeScope = !showTabs && includePast ? "all" : "current";
  const queryKey = JSON.stringify([showTabs, includePast, from, to, sort]);
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;
  const requestGeneration = useRef(0);
  const loadingMoreRef = useRef(false);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<ListingState>(() => ({
    error: null,
    events: (usesSearch ? initialPage?.events : initialEvents) ?? null,
    hasMore: usesSearch && initialPage ? initialPage.hasMore : false,
    loading: true,
    loadingMore: false,
  }));

  useEffect(() => {
    let active = true;
    requestGeneration.current += 1;
    loadingMoreRef.current = false;
    setState((current) => ({ ...current, loading: true, loadingMore: false }));
    const request = usesSearch
      ? client.search({
          from,
          limit: 20,
          offset: 0,
          sort: searchSort,
          timeScope,
          to,
        })
      : client.list().then((events) => ({ events, hasMore: false }));
    request.then(
      (page) => {
        if (active) {
          setState({
            error: null,
            events: page.events,
            hasMore: page.hasMore,
            loading: false,
            loadingMore: false,
          });
        }
      },
      (error: LockerverseSdkError) => {
        if (active) {
          setState((current) => ({
            ...current,
            error,
            loading: false,
            loadingMore: false,
          }));
        }
      }
    );
    return () => {
      active = false;
    };
  }, [client, from, to, timeScope, searchSort, usesSearch, revision]);

  const refresh = useCallback(() => {
    requestGeneration.current += 1;
    loadingMoreRef.current = false;
    setState((current) => ({ ...current, error: null, loading: true }));
    setRevision((current) => current + 1);
  }, []);

  const loadMore = useCallback(() => {
    if (
      !(usesSearch && state.hasMore) ||
      state.loading ||
      loadingMoreRef.current
    ) {
      return;
    }
    const offset = state.events?.length ?? 0;
    const generation = requestGeneration.current;
    loadingMoreRef.current = true;
    setState((current) => ({ ...current, error: null, loadingMore: true }));
    client
      .search({ from, limit: 20, offset, sort: searchSort, timeScope, to })
      .then(
        (page) => {
          if (
            queryKeyRef.current !== queryKey ||
            requestGeneration.current !== generation
          ) {
            return;
          }
          loadingMoreRef.current = false;
          setState((current) => ({
            ...current,
            events: [...(current.events ?? []), ...page.events],
            hasMore: page.hasMore,
            loadingMore: false,
          }));
        },
        (error: LockerverseSdkError) => {
          if (
            queryKeyRef.current !== queryKey ||
            requestGeneration.current !== generation
          ) {
            return;
          }
          loadingMoreRef.current = false;
          setState((current) => ({ ...current, error, loadingMore: false }));
        }
      );
  }, [client, from, to, timeScope, searchSort, usesSearch, state, queryKey]);

  return {
    ...state,
    client,
    config: { from, includePast, showTabs, sort, to },
    loadMore,
    refresh,
  };
}
